import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';

import { FlexPeriodService, UpdateFlexPeriodRequest } from '../../services/flex-period.service';
import { FlexVisibilityService } from '../../services/flex-visibility.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import {
	DeleteFlexPeriodFailureAction,
	DeleteFlexPeriodParams,
	FlexPeriodActionTypes,
	FlexPeriodUpdateParams,
	GetFlexPeriodsFailureAction,
	GetFlexPeriodsSuccessAction,
	NoopAction,
	UpdateFlexPeriodFailureAction,
	UpdateFlexPeriodSuccessAction,
} from './flex-periods.actions';

@Injectable()
export class FlexPeriodEffects {
	loadFlexPeriods$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(FlexPeriodActionTypes.GetFlexPeriods),
			exhaustMap(() => {
				return this.flexPeriodService.GetAllFlexPeriodsHTTP().pipe(
					map((flexPeriods) => GetFlexPeriodsSuccessAction({ flexPeriods })),
					catchError((error) => of(GetFlexPeriodsFailureAction({ errorMessage: error })))
				);
			})
		);
	});

	updateFlexPeriod$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(FlexPeriodActionTypes.UpdateFlexPeriod),
			exhaustMap((action: Action & FlexPeriodUpdateParams) => {
				const updateBody: UpdateFlexPeriodRequest = {
					id: action.update.id,
					...action.update.changes,
					update_visibility_students_groups: action.update_visibility_students_groups,
				};
				return this.flexPeriodService.EditFlexPeriodHTTP(updateBody).pipe(
					map((resp) => {
						return UpdateFlexPeriodSuccessAction({ updated: resp });
					}),
					catchError((_error) => {
						this.toastService.openToast({
							title: `Issue encountered while updating flex period '${action.original?.name}', try again`,
							type: 'error',
						});
						if (action.original) {
							return of(UpdateFlexPeriodFailureAction({ original: action.original }));
						} else {
							return of(NoopAction());
						}
					})
				);
			})
		);
	});

	deleteFlexPeriod$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(FlexPeriodActionTypes.DeleteFlexPeriod),
			exhaustMap((action: Action & DeleteFlexPeriodParams) => {
				return this.flexPeriodService.DeleteFlexPeriodHTTP(action.id).pipe(
					map(() => {
						this.toastService.openToast({ title: `Flex period '${action.toRemove?.name}' removed`, type: 'success' });
						return NoopAction();
					}),
					catchError((_error) => {
						this.toastService.openToast({
							title: `Issue encountered while deleting flex period '${action.toRemove?.name}', try again`,
							type: 'error',
						});
						if (action.toRemove) {
							return of(DeleteFlexPeriodFailureAction({ flexPeriod: action.toRemove }));
						} else {
							return of(NoopAction());
						}
					})
				);
			})
		);
	});

	updateFlexPeriodStudentLists$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(UpdateFlexPeriodSuccessAction),
				switchMap(({ updated }) =>
					this.userService.getStudentListsByEntityId(updated.id, 'period').pipe(
						tap((studentLists) => {
							this.flexVisibilityService.existingSelectedFlexGroups$.next(studentLists);
						})
					)
				)
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private flexPeriodService: FlexPeriodService,
		private toastService: ToastService,
		private userService: UserService,
		private flexVisibilityService: FlexVisibilityService
	) {}
}
